"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContentI18nPolicyError = void 0;
exports.resolveContentI18nAppScopes = resolveContentI18nAppScopes;
exports.contentI18nAppScopeOfRoute = contentI18nAppScopeOfRoute;
exports.applyContentI18nToSources = applyContentI18nToSources;
const app_base_config_1 = require("./app-base-config");
const app_base_contract_1 = require("./app-base-contract");
const content_i18n_1 = require("./content-i18n");
const I18N_CELL_KEY = '__i18n';
/**
 * One scope per mounted App that brings a translation table, read from what
 * the App Container already stores: the App's resources, the mount bindings
 * (name in the App -> key in the container) and the role of the bound table.
 */
function resolveContentI18nAppScopes(config) {
    const scopes = [];
    for (const mount of config.appComposition?.mounts ?? []) {
        const bound = [
            ...new Set([
                ...(config.appDefinitions?.[mount.appId]?.resources ?? []).map(resource => resource.sourceKey),
                ...Object.keys(mount.bindings),
            ]),
        ].map(appSourceKey => ({
            appSourceKey,
            sourceKey: mount.bindings[appSourceKey]?.targetSourceKey ?? appSourceKey,
        }));
        const translations = bound.find(({ sourceKey }) => config.databaseBindings?.[sourceKey]?.role === 'translations');
        if (!translations)
            continue;
        scopes.push({
            mountId: mount.mountId,
            routePrefix: mount.routePrefix,
            translationsSourceKey: translations.sourceKey,
            appSourceKeys: Object.fromEntries(bound
                .filter(({ sourceKey }) => sourceKey !== translations.sourceKey)
                .map(({ appSourceKey, sourceKey }) => [sourceKey, appSourceKey])),
        });
    }
    return scopes;
}
/**
 * The App whose translation table localizes `sourceKey` on the page
 * `routePath`: the App the page belongs to when it reads that table, else the
 * first App that reads it. Null where no App with a translation table reads it.
 */
function contentI18nAppScopeOfRoute(scopes, routePath, sourceKey) {
    const reading = scopes.filter(scope => sourceKey in scope.appSourceKeys);
    return (0, app_base_contract_1.mountOfRoute)(reading, routePath) ?? reading[0] ?? null;
}
class ContentI18nPolicyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ContentI18nPolicyError';
    }
}
exports.ContentI18nPolicyError = ContentI18nPolicyError;
function applyContentI18nToSources(input) {
    if (input.locale === input.defaultLocale)
        return input.sources;
    const apps = input.apps ?? [];
    const translationKeys = new Set([
        app_base_config_1.APP_BASE_DATA_SOURCE_KEYS.TRANSLATIONS,
        ...apps.map(app => app.translationsSourceKey),
    ]);
    const entriesByTable = new Map();
    const entriesOf = (translationsSourceKey) => {
        const known = entriesByTable.get(translationsSourceKey);
        if (known)
            return known;
        const translations = input.sources[translationsSourceKey];
        const byEntity = new Map();
        for (const entry of translations
            ? (0, content_i18n_1.parseContentTranslationRows)(translations.rows, translations.columns)
            : []) {
            if (entry.locale !== input.locale ||
                !(0, content_i18n_1.isPublishedContentTranslation)(entry))
                continue;
            const current = byEntity.get(entry.entityDb) ?? [];
            current.push(entry);
            byEntity.set(entry.entityDb, current);
        }
        entriesByTable.set(translationsSourceKey, byEntity);
        return byEntity;
    };
    return Object.fromEntries(Object.entries(input.sources).map(([sourceKey, source]) => {
        if (translationKeys.has(sourceKey))
            return [sourceKey, source];
        // Apps that share a table and a translation table name it once. Entries
        // apply in mount order, so the App mounted last wins each field: its
        // translation table moves behind every table named before.
        const namesByTable = new Map();
        for (const app of apps) {
            const appSourceKey = app.appSourceKeys[sourceKey];
            if (appSourceKey === undefined)
                continue;
            const names = namesByTable.get(app.translationsSourceKey) ?? new Set();
            names.add(appSourceKey);
            namesByTable.delete(app.translationsSourceKey);
            namesByTable.set(app.translationsSourceKey, names);
        }
        if (namesByTable.size === 0)
            namesByTable.set(app_base_config_1.APP_BASE_DATA_SOURCE_KEYS.TRANSLATIONS, new Set());
        const allEntries = [...namesByTable].flatMap(([translationsSourceKey, names]) => {
            const byEntity = entriesOf(translationsSourceKey);
            return [
                ...new Set([sourceKey, source.source.blockId, ...names]),
            ].flatMap(entityDb => byEntity.get(entityDb) ?? []);
        });
        // Entries naming select options label the column, never a row.
        const sourceEntries = allEntries.filter(entry => (0, content_i18n_1.parseContentI18nOptionField)(entry.field) === null);
        validateCoverage({
            source,
            entries: sourceEntries,
            locale: input.locale,
            policy: input.policy ?? content_i18n_1.DEFAULT_CONTENT_I18N_POLICY,
        });
        if (allEntries.length === 0 && source.source.role !== 'content') {
            return [sourceKey, source];
        }
        return [
            sourceKey,
            {
                ...source,
                columns: applyOptionEntriesToColumns(source.columns, allEntries),
                rows: sourceEntries.length === 0 && source.source.role !== 'content'
                    ? source.rows
                    : applyEntriesToRows(source.rows, source.columns, sourceEntries, input.locale, input.defaultLocale),
            },
        ];
    }));
}
function validateCoverage(input) {
    if (input.policy.missingTranslation !== 'strict')
        return;
    const translated = new Set(input.entries
        .filter(entry => entry.locale === input.locale)
        .filter(content_i18n_1.isPublishedContentTranslation)
        .filter(entry => hasContent(entry.value))
        .map(entry => {
        const fieldKey = resolveContentFieldKey(entry.field, input.source.columns);
        return fieldKey
            ? `${entry.entityRowId}\u0000${fieldKey}`
            : `invalid\u0000${entry.field}`;
    }));
    for (const row of input.source.rows) {
        for (const column of input.source.columns) {
            if (!(0, content_i18n_1.isContentI18nTranslatableColumn)(column) ||
                !hasContent(row.cells[column.id])) {
                continue;
            }
            if (!translated.has(`${row.id}\u0000${column.id}`)) {
                throw new ContentI18nPolicyError(`Missing ${input.locale} translation for row "${row.id}" field "${column.name}"`);
            }
        }
    }
}
/**
 * Labels of select and multi-select options in the requested locale. Cells
 * keep the option ID, so filters, sorting and grouping are unchanged; an
 * option without a translation keeps its source label.
 */
function applyOptionEntriesToColumns(columns, entries) {
    const labels = new Map();
    for (const entry of entries) {
        const columnId = (0, content_i18n_1.parseContentI18nOptionField)(entry.field);
        if (columnId === null || !hasContent(entry.value))
            continue;
        labels.set(`${columnId}\u0000${entry.entityRowId}`, entry.value);
    }
    if (labels.size === 0)
        return columns;
    return columns.map(column => {
        const data = column.data;
        if ((column.type !== 'select' && column.type !== 'multi-select') ||
            !data ||
            typeof data !== 'object' ||
            !Array.isArray(data.options)) {
            return column;
        }
        const options = data.options;
        let translated = 0;
        const localized = options.map((option) => {
            if (!option || typeof option !== 'object')
                return option;
            const label = labels.get(`${column.id}\u0000${String(option.id)}`);
            if (label === undefined)
                return option;
            translated += 1;
            return { ...option, value: label };
        });
        return translated === 0
            ? column
            : { ...column, data: { ...data, options: localized } };
    });
}
function applyEntriesToRows(rows, columns, entries, locale, defaultLocale) {
    const byRow = new Map();
    for (const entry of entries) {
        const current = byRow.get(entry.entityRowId) ?? [];
        current.push(entry);
        byRow.set(entry.entityRowId, current);
    }
    return rows.map(row => {
        const rowEntries = byRow.get(row.id) ?? [];
        const requiredFields = columns
            .filter(column => (0, content_i18n_1.isContentI18nTranslatableColumn)(column))
            .filter(column => hasContent(row.cells[column.id]))
            .map(column => column.id);
        const translatedFields = new Set(rowEntries
            .filter(entry => hasContent(entry.value))
            .map(entry => resolveContentFieldKey(entry.field, columns))
            .filter((field) => Boolean(field)));
        const missingFields = requiredFields.filter(field => !translatedFields.has(field));
        if (missingFields.length > 0) {
            return {
                ...row,
                cells: {
                    ...row.cells,
                    [content_i18n_1.CONTENT_I18N_ROW_STATE_KEY]: {
                        state: 'fallback',
                        requestedLocale: locale,
                        resolvedLocale: defaultLocale,
                        defaultLocale,
                        missingFields,
                    },
                },
            };
        }
        const cells = { ...row.cells };
        const i18n = { ...readI18nCell(cells) };
        let applied = 0;
        for (const entry of rowEntries) {
            if (!hasContent(entry.value))
                continue;
            const fieldKey = resolveContentFieldKey(entry.field, columns);
            if (!fieldKey)
                continue;
            const column = columns.find(candidate => candidate.id === fieldKey);
            if (!column || !(0, content_i18n_1.isContentI18nTranslatableColumn)(column)) {
                continue;
            }
            cells[fieldKey] = entry.value;
            i18n[fieldKey] = { ...i18n[fieldKey], [locale]: entry.value };
            applied += 1;
        }
        if (applied === 0)
            return row;
        cells[I18N_CELL_KEY] = i18n;
        cells[content_i18n_1.CONTENT_I18N_ROW_STATE_KEY] = {
            state: 'translated',
            requestedLocale: locale,
            resolvedLocale: locale,
            defaultLocale,
            missingFields: [],
        };
        return { ...row, cells };
    });
}
function resolveContentFieldKey(field, columns) {
    return (columns.find(column => column.id === field)?.id ??
        columns.find(column => column.name === field)?.id ??
        null);
}
function hasContent(value) {
    return value !== null && value !== undefined && value !== '';
}
function readI18nCell(cells) {
    const raw = cells[I18N_CELL_KEY];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return {};
    return raw;
}
